'use client'

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useMutation } from '@apollo/client'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Plus } from 'lucide-react'
import { ContentBlockSelector } from './components/ContentBlockSelector'
import { DraggableContentBlockList } from './components/DraggableContentBlockList'
import InlineContentBlockForm from './components/InlineContentBlockForm'
import { RemoveBlockDialog } from './components/RemoveBlockDialog'
import {
  ContentBlockItem,
} from './components/DraggableContentBlockItem'
import {
  DELETE_MANY_CONTENT_BLOCKS,
  UPDATE_CONTENT_BLOCK_ARRANGEMENT,
} from './contentBlockQueries'
import {
  ContentBlocksManagerHandle,
  ContentBlocksManagerProps,
} from './types'

function getBlockName(block: ContentBlockItem) {
  const profile = block.content_block_profiles.find(p => p.language === 'en')
  return (
    profile?.name ||
    block.content_block_profiles[0]?.name ||
    `Block #${block.id}`
  )
}

export const ContentBlocksManager = forwardRef<
  ContentBlocksManagerHandle,
  ContentBlocksManagerProps
>(function ContentBlocksManager(
  {
    owner: _owner,
    initialBlocks,
    enableInlineCreate = true,
    enableEditExisting = false,
    enablePermanentDelete = true,
    title = 'Content Blocks',
    emptyText = 'No content blocks assigned.',
  },
  ref
) {
  const [blocks, setBlocks] = useState<ContentBlockItem[]>(() =>
    [...initialBlocks].sort(
      (a, b) => (a.arrangement ?? 0) - (b.arrangement ?? 0)
    ) as ContentBlockItem[]
  )
  const [selectorOpen, setSelectorOpen] = useState(false)
  const [inlineFormOpen, setInlineFormOpen] = useState(false)
  const [editingBlockId, setEditingBlockId] = useState<number | null>(null)
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
  const [blockToRemove, setBlockToRemove] = useState<ContentBlockItem | null>(
    null
  )

  const blocksRef = useRef<ContentBlockItem[]>(blocks)
  useEffect(() => {
    blocksRef.current = blocks
  }, [blocks])

  useEffect(() => {
    setBlocks(
      [...initialBlocks].sort(
        (a, b) => (a.arrangement ?? 0) - (b.arrangement ?? 0)
      ) as ContentBlockItem[]
    )
  }, [initialBlocks])

  const [updateContentBlockArrangement] = useMutation(
    UPDATE_CONTENT_BLOCK_ARRANGEMENT
  )
  const [deleteManyContentBlocks] = useMutation(DELETE_MANY_CONTENT_BLOCKS)

  useImperativeHandle(
    ref,
    () => ({
      getBlockIds: () => blocksRef.current.map(b => ({ id: b.id })),
      commitArrangement: async () => {
        const current = blocksRef.current
        for (let i = 0; i < current.length; i++) {
          await updateContentBlockArrangement({
            variables: {
              where: { id: current[i].id },
              data: { arrangement: { set: i } },
            },
          })
        }
        setBlocks(prev => prev.map((b, i) => ({ ...b, arrangement: i })))
      },
    }),
    [updateContentBlockArrangement]
  )

  const handleReorder = (reorderedBlocks: ContentBlockItem[]) => {
    setBlocks(reorderedBlocks)
  }

  const handleAddBlock = (block: ContentBlockItem) => {
    setBlocks(prev => [...prev, { ...block, arrangement: prev.length }])
    setSelectorOpen(false)
  }

  const handleRemove = (blockId: number) => {
    if (!enablePermanentDelete) {
      setBlocks(prev => {
        const filtered = prev.filter(b => b.id !== blockId)
        return filtered.map((block, index) => ({
          ...block,
          arrangement: index,
        }))
      })
      return
    }
    const block = blocks.find(b => b.id === blockId)
    if (block) {
      setBlockToRemove(block)
      setRemoveDialogOpen(true)
    }
  }

  const handleRemoveFromOwner = () => {
    if (blockToRemove) {
      setBlocks(prev => prev.filter(b => b.id !== blockToRemove.id))
      setBlockToRemove(null)
      setRemoveDialogOpen(false)
    }
  }

  const handleDeletePermanently = async () => {
    if (blockToRemove) {
      try {
        await deleteManyContentBlocks({
          variables: { ids: [blockToRemove.id] },
        })
        setBlocks(prev => prev.filter(b => b.id !== blockToRemove.id))
        toast.success('Content block deleted permanently')
        setRemoveDialogOpen(false)
      } catch {
        toast.error('Failed to delete content block')
      }
      setBlockToRemove(null)
    }
  }

  const handleEditBlock = (blockId: number) => {
    setEditingBlockId(blockId)
    setInlineFormOpen(true)
  }

  const handleCreateNew = () => {
    setEditingBlockId(null)
    setInlineFormOpen(true)
  }

  const handleInlineCreated = (block: ContentBlockItem) => {
    setBlocks(prev => [...prev, { ...block, arrangement: prev.length }])
  }

  const handleInlineUpdated = (block: ContentBlockItem) => {
    setBlocks(prev =>
      prev.map(b =>
        b.id === block.id ? { ...b, ...block, arrangement: b.arrangement } : b
      )
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">{title}</h3>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setSelectorOpen(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Content Block
        </Button>
      </div>

      {blocks.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">
          {emptyText}
        </p>
      ) : (
        <DraggableContentBlockList
          blocks={blocks}
          onReorder={handleReorder}
          onRemove={handleRemove}
          onEdit={
            enableInlineCreate || enableEditExisting
              ? handleEditBlock
              : undefined
          }
        />
      )}

      <ContentBlockSelector
        open={selectorOpen}
        onOpenChange={setSelectorOpen}
        assignedBlockIds={blocks.map(b => b.id)}
        onSelect={handleAddBlock}
        onCreateNew={enableInlineCreate ? handleCreateNew : undefined}
      />

      {(enableInlineCreate || enableEditExisting) && (
        <InlineContentBlockForm
          open={inlineFormOpen}
          onOpenChange={setInlineFormOpen}
          blockId={editingBlockId}
          onCreated={handleInlineCreated}
          onUpdated={handleInlineUpdated}
        />
      )}

      {enablePermanentDelete && (
        <RemoveBlockDialog
          open={removeDialogOpen}
          onOpenChange={setRemoveDialogOpen}
          blockName={blockToRemove ? getBlockName(blockToRemove) : ''}
          onRemoveFromPage={handleRemoveFromOwner}
          onDeletePermanently={handleDeletePermanently}
        />
      )}
    </div>
  )
})
